import React from 'react';
import { Trophy, Users, BarChart3, Star, AlertCircle, RefreshCw } from 'lucide-react';
import { useGetSurveySummaryQuery } from '../../../../store/apis/survey.api';
import { Button } from '../../../common';
import type { IQuestionSummaryItem } from '../../../../types';

interface SurveySummaryTabProps {
  surveyId: string;
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  rating: 'Rating',
  mcq: 'Single choice',
  checkbox: 'Multi select',
  short_text: 'Short text',
  long_text: 'Long text',
  date: 'Date',
  number: 'Number',
};

const getRankBadgeStyle = (rank: number) => {
  if (rank === 1) {
    return {
      bg: 'bg-[var(--color-warning-light)] text-[var(--color-warning-dark)] border-[var(--color-warning-light)]',
      badge: 'bg-[var(--color-warning)] text-white',
      cardBorder: 'border-[var(--color-warning-light)]',
      highlightLabel: '#1 Top Rated Question',
    };
  }
  return {
    bg: 'bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)] border-[var(--color-border-tertiary)]',
    badge: 'bg-[var(--color-text-tertiary)] text-white',
    cardBorder: 'border-[var(--color-border-primary)]',
    highlightLabel: rank === 2 ? '#2 Most Responded' : rank === 3 ? '#3 Most Responded' : `#${rank}`,
  };
};

export const SurveySummaryTab: React.FC<SurveySummaryTabProps> = ({ surveyId }) => {
  const { data, isLoading, isError, error, refetch } = useGetSurveySummaryQuery(surveyId, {
    skip: !surveyId,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {[1, 2, 3].map((n) => (
          <div
            key={n}
            className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card p-5 animate-pulse"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[var(--color-bg-muted)]" />
              <div className="h-5 bg-[var(--color-bg-muted)] rounded w-1/2" />
            </div>
            <div className="h-4 bg-[var(--color-bg-muted)] rounded w-1/4 mb-4" />
            <div className="h-3 bg-[var(--color-bg-muted)] rounded w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
        <AlertCircle className="w-10 h-10 text-[var(--color-error)] mx-auto mb-3" />
        <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">
          Failed to load summary insights
        </h3>
        <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
          {typeof error === 'string' ? error : 'An unexpected error occurred while fetching question ratings.'}
        </p>
        <Button
          appearance="primary"
          size="small"
          onClick={() => refetch()}
          icon={<RefreshCw className="w-4 h-4" />}
        >
          Retry
        </Button>
      </div>
    );
  }

  const questions = data?.questions || [];
  const totalSubmissions = data?.totalCompletedSubmissions || 0;

  if (questions.length === 0) {
    return (
      <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
        <BarChart3 className="w-10 h-10 text-[var(--color-text-tertiary)] mx-auto mb-3" />
        <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">
          No question analytics available
        </h3>
        <p className="text-body-sm text-[var(--color-text-secondary)] max-w-md mx-auto">
          This survey does not have questions or no responses have been recorded yet.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Top Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-xs)]">
        <div>
          <h2 className="text-heading-sm font-bold text-[var(--color-text-primary)]">
            Question Ratings & Ranking
          </h2>
          <p className="text-body-xs text-[var(--color-text-secondary)] mt-0.5">
            Ranked by participation volume — questions with the most responses appear at the top.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-caption font-semibold bg-[var(--color-primary-100)] text-[var(--color-primary-700)]">
            <Users className="w-3.5 h-3.5" />
            {totalSubmissions} {totalSubmissions === 1 ? 'completed response' : 'completed responses'}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-caption font-semibold bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]">
            <BarChart3 className="w-3.5 h-3.5" />
            {questions.length} {questions.length === 1 ? 'question' : 'questions'}
          </span>
        </div>
      </div>

      {/* Ranked Questions List */}
      <div className="flex flex-col gap-3.5">
        {questions.map((question: IQuestionSummaryItem) => {
          const rankInfo = getRankBadgeStyle(question.rank);
          const typeLabel = QUESTION_TYPE_LABELS[question.type] || question.type;
          const isTopRank = question.rank === 1;

          return (
            <div
              key={question.questionId}
              className={`bg-[var(--color-bg-base)] border rounded-card p-5 transition-all duration-150 ${rankInfo.cardBorder}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Rank Badge */}
                  <div
                    className={`w-9 h-9 rounded-xl flex-none grid place-items-center font-extrabold text-[13px] border ${rankInfo.bg}`}
                  >
                    {isTopRank ? <Trophy className="w-4 h-4 text-[var(--color-warning-dark)]" /> : `#${question.rank}`}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {isTopRank && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-[var(--color-warning-light)] text-[var(--color-warning-dark)]">
                          <Trophy className="w-3 h-3" />
                          Top Rated Question
                        </span>
                      )}
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]">
                        {typeLabel}
                      </span>
                      {question.type === 'rating' && question.maxRating && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-[var(--color-warning-light)] text-[var(--color-warning-dark)]">
                          <Star className="w-3 h-3 fill-[var(--color-warning)] stroke-[var(--color-warning)]" />
                          Max {question.maxRating}
                        </span>
                      )}
                    </div>

                    <h3 className="text-body-md font-bold text-[var(--color-text-primary)] leading-snug">
                      {question.questionText}
                    </h3>
                  </div>
                </div>

                {/* Response Count Pill */}
                <div className="text-right flex-none">
                  <div className="text-heading-sm font-extrabold text-[var(--color-text-primary)]">
                    {question.responseCount}
                  </div>
                  <div className="text-[11px] font-medium text-[var(--color-text-tertiary)]">
                    {question.responseCount === 1 ? 'response' : 'responses'}
                  </div>
                </div>
              </div>

              {/* Participation Bar */}
              <div className="mt-3.5 pt-3 border-t border-[var(--color-border-tertiary)]">
                <div className="flex items-center justify-between text-caption font-semibold mb-1.5">
                  <span className="text-[var(--color-text-secondary)]">
                    Participation Rate
                  </span>
                  <span className="text-[var(--color-text-primary)] font-bold">
                    {question.responseRate}% ({question.responseCount} of {totalSubmissions})
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-[var(--color-bg-muted)] overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${isTopRank
                      ? 'bg-[var(--color-warning)]'
                      : question.responseRate >= 80
                        ? 'bg-[var(--color-success)]'
                        : question.responseRate >= 50
                          ? 'bg-[var(--color-primary-600)]'
                          : 'bg-[var(--color-warning)]'
                      }`}
                    style={{ width: `${Math.min(question.responseRate, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SurveySummaryTab;
